import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Package, Truck, Home } from "lucide-react";

export default function OrderSuccess() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orders/${orderNumber}`)
      .then((r) => setOrder(r.data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderNumber]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-24 text-center">Loading...</div>;
  if (!order) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center">
      <p className="font-serif text-2xl">Order not found</p>
      <Link to="/" className="text-[#9E3B21] font-semibold">Back home</Link>
    </div>
  );

  return (
    <div className="fade-up max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <div className="text-center mb-10">
        <div className="w-16 h-16 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-9 h-9 text-[#2D6A4F]" />
        </div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#9E3B21] font-semibold mb-2">✦ Order Confirmed</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold">Shukriya, {order.address.full_name.split(" ")[0]}!</h1>
        <p className="mt-3 text-[#6E675F]">
          Your order <span className="font-mono font-semibold text-[#1E1B18]" data-testid="order-number">{order.order_number}</span> is confirmed.
          {order.payment_method === "cod" ? " We'll call you soon and deliver in 2–4 days." : ""}
        </p>
      </div>

      {/* Steps */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        {[
          { icon: CheckCircle2, label: "Confirmed", active: true },
          { icon: Package, label: "Preparing", active: false },
          { icon: Truck, label: "Out for Delivery", active: false },
        ].map((s, i) => (
          <div key={i} className={`p-4 rounded-2xl border ${s.active ? "border-[#9E3B21] bg-[#9E3B21]/5" : "border-[#E6DEC3] bg-white"}`}>
            <s.icon className={`w-5 h-5 mb-2 ${s.active ? "text-[#9E3B21]" : "text-[#6E675F]"}`} />
            <div className="text-xs font-semibold">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Details */}
      <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6 mb-6">
        <h2 className="font-serif text-xl font-semibold mb-4">Items</h2>
        <ul className="space-y-3 mb-5">
            {order.items.map((i, idx) => (
              <li key={idx} className="text-sm">
                <div className="flex justify-between">
                  <div>
                    <div className="font-medium">{i.product_name}</div>
                    <div className="text-xs text-[#6E675F]">{i.weight} × {i.quantity}</div>
                  </div>
                  <div className="font-semibold">{formatINR(i.unit_price * i.quantity)}</div>
                </div>
                {i.is_gift_box && i.gift_note ? (
                  <div className="mt-1.5 ml-0 text-xs text-[#5F7355] italic bg-[#5F7355]/5 rounded-lg px-3 py-2">
                    Gift note: "{i.gift_note}"
                  </div>
                ) : null}
              </li>
            ))}
        </ul>
        <div className="border-t border-[#E6DEC3] pt-4 space-y-2 text-sm">
          <div className="flex justify-between text-[#6E675F]"><span>Subtotal</span><span>{formatINR(order.subtotal)}</span></div>
          <div className="flex justify-between text-[#6E675F]"><span>Shipping</span><span>{order.shipping === 0 ? "FREE" : formatINR(order.shipping)}</span></div>
          {order.discount ? (
            <div className="flex justify-between text-[#2D6A4F]"><span>Coupon {order.coupon_code}</span><span>− {formatINR(order.discount)}</span></div>
          ) : null}
          <div className="flex justify-between pt-2 border-t border-[#E6DEC3] font-serif text-lg font-semibold">
            <span>Total ({order.payment_method === "cod" ? "Cash on Delivery" : order.payment_method})</span>
            <span>{formatINR(order.total)}</span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6 mb-8">
        <h2 className="font-serif text-xl font-semibold mb-3">Delivering to</h2>
        <p className="text-sm text-[#4A423C] leading-relaxed">
          <span className="font-semibold">{order.address.full_name}</span> · +91 {order.address.phone}<br />
          {order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}<br />
          {order.address.city}, {order.address.state} - {order.address.pincode}
          {order.address.landmark ? <><br />Landmark: {order.address.landmark}</> : null}
        </p>
        {order.address.email && order.email_sent ? (
          <p className="text-xs text-[#5F7355] mt-3">✓ A confirmation has also been sent to {order.address.email}</p>
        ) : order.address.email ? (
          <p className="text-xs text-[#6E675F] mt-3">
            <span className="font-semibold text-[#5F7355]">✓ Order confirmed.</span> Your email confirmation to <span className="font-medium">{order.address.email}</span> may take a few minutes to arrive. Your order details are safe above — we've already got your order and we'll call you to confirm delivery.
          </p>
        ) : null}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/shop">
          <Button data-testid="os-continue-shopping" className="btn-primary rounded-full px-8 py-6">
            <Home className="w-4 h-4 mr-2" /> Continue shopping
          </Button>
        </Link>
        <a
          data-testid="os-email-mailto"
          href={buildMailto(order)}
          className="inline-flex items-center justify-center rounded-full px-8 py-3 border-2 border-[#1E1B18] hover:bg-[#1E1B18] hover:text-[#FBF8F3] transition-colors text-sm font-medium"
        >
          Email me these details
        </a>
        <Link to="/my-orders">
          <Button data-testid="os-my-orders" variant="outline" className="rounded-full px-8 py-6">
            Track & reorder
          </Button>
        </Link>
      </div>
    </div>
  );
}

function buildMailto(order) {
  const subject = `My Dadu's Achaar order ${order.order_number}`;
  const lines = [
    `Order: ${order.order_number}`,
    `Placed: ${new Date(order.created_at).toLocaleString()}`,
    ``,
    `Items:`,
    ...order.items.map((i) => `- ${i.product_name} · ${i.weight} × ${i.quantity} = Rs.${i.unit_price * i.quantity}`),
    ``,
    `Subtotal: Rs.${order.subtotal}`,
    `Shipping: ${order.shipping === 0 ? "FREE" : "Rs." + order.shipping}`,
    `Total (${order.payment_method.toUpperCase()}): Rs.${order.total}`,
    ``,
    `Delivering to:`,
    order.address.full_name,
    order.address.line1 + (order.address.line2 ? ", " + order.address.line2 : ""),
    `${order.address.city}, ${order.address.state} - ${order.address.pincode}`,
    `Phone: +91 ${order.address.phone}`,
  ].join("\n");
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines)}`;
}
